import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import platform from '../../lib/platform';
import { cat_id2lbl } from '../../lib/cat';
import { useAuthStore } from '../../store/authStore';
import BrandHeader from '../../components/brand/BrandHeader';

import xdate from '../../../../../util/date.js';

type Campaign = {
  id: string;
  name: string;
  description: string;
  category: string;
  status?: string;
  budget_info?: {
    creator_tiers?: Record<string, {enabled?: boolean, payment?: number}>;
    total_budget: number;
    spent: number;
    remaining: number;
  };
  targeting?: {
    countries?: string[];
  };
  allowed_ad_formats?: string[];
  is_active?: boolean;
  ts?: {insert: Date, modified: Date};
  active_creators_count?: number;
};

export default function CampaignsList() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive' | 'draft'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const { user } = useAuthStore();

  useEffect(() => {
    if (user)
      fetchCampaigns();
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const result = await back_app.campaign_list();
      console.log('campaign list result', result);
      if (!result.err) {
        const campaignsData = Array.isArray(result) ? result : [];
        
        
        const campaignsWithCounts = await Promise.all(
          campaignsData.map(async (campaign: Campaign) => {
            try {
              const creatorsResult = await back_app.vcamp_list({campaign_id: campaign.id});
              return {
                ...campaign,
                active_creators_count: !creatorsResult.err && Array.isArray(creatorsResult) ? creatorsResult.length : 0
              };
            } catch (error) {
              console.error(`Error fetching creators for campaign ${campaign.id}:`, error);
              return { ...campaign, active_creators_count: 0 };
            }
          })
        );
        
        setCampaigns(campaignsWithCounts);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(campaign => {
      const matchesSearch = (campaign.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (campaign.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus =
        filterStatus === 'all' ? true :
        filterStatus === 'draft' ? campaign.status === 'draft' :
        filterStatus === 'active' ? campaign.status == 'active' :
        campaign.status != 'active' && campaign.status != 'draft';

      const matchesCategory = filterCategory === 'all' || campaign.category === filterCategory;

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [campaigns, searchTerm, filterStatus, filterCategory]);

  const delete_draft = async (campaign_id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Delete this draft?'))
      return;
    try {
      let res = await back_app.campaign_delete(campaign_id);
      if (!res.err)
        setCampaigns(prev => prev.filter(c => c.id !== campaign_id));
    } catch (err) {
      console.error('delete draft failed', err);
    }
  };

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status == 'active').length,
    inactive: campaigns.filter(c => c.status != 'active' && c.status != 'draft').length,
    draft: campaigns.filter(c => c.status === 'draft').length,
  };

  const categories = ['all', ...new Set(campaigns.map(c => c.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Campaigns</h2>
            <p className="text-gray-600 mt-1">
              {filteredCampaigns.length} {filteredCampaigns.length === 1 ? 'campaign' : 'campaigns'}
            </p>
          </div>
          <button
            onClick={() => navigate('/brand/create-campaign')}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
          >
            + Create Campaign
          </button>
        </div>

        {}
        <div className="bg-white rounded-xl border p-4 mb-6 space-y-4">
          {}
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />

          {}
          <div className="flex flex-col sm:flex-row gap-3">
            {}
            <div className="flex gap-2 overflow-x-auto">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  filterStatus === 'all'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All ({stats.total})
              </button>
              <button
                onClick={() => setFilterStatus('active')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  filterStatus === 'active'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Active ({stats.active})
              </button>
              <button
                onClick={() => setFilterStatus('draft')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  filterStatus === 'draft'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Drafts ({stats.draft})
              </button>
              <button
                onClick={() => setFilterStatus('inactive')}
                className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  filterStatus === 'inactive'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Inactive ({stats.inactive})
              </button>
            </div>

            {}
            {categories.length > 1 && (
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : cat_id2lbl(category)}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="text-6xl mb-4">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all' ? '🔍' : '📋'}
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all' ? 'No campaigns found' : 'No campaigns yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filterStatus !== 'all' || filterCategory !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'Create your first campaign to get started'}
            </p>
            {!searchTerm && filterStatus === 'all' && filterCategory === 'all' && (
              <button
                onClick={() => navigate('/brand/create-campaign')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Create First Campaign
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCampaigns.map((campaign) => {
              const spent = campaign.budget_info?.spent || 0;
              const totalBudget = campaign.budget_info?.total_budget || 0;
              const remaining = campaign.budget_info?.remaining || 0;
              const spentPercentage = totalBudget > 0 ? (spent / totalBudget) * 100 : 0;

              return (
                <div
                  key={campaign.id}
                  className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => campaign.status === 'draft'
                    ? navigate(`/brand/create-campaign/${campaign.id}`)
                    : navigate(`/brand/campaigns/${campaign.id}`)}
                >
                  {}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-gray-900 text-lg flex-1 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {campaign.name || 'Untitled Draft'}
                    </h3>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          campaign.status === 'draft'
                            ? 'bg-yellow-400 text-yellow-900'
                            : campaign.status == 'active'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-400 text-white'
                        }`}
                      >
                        {campaign.status === 'draft' ? 'Draft' : campaign.status == 'active' ? 'Active' : 'Inactive'}
                      </span>
                      {campaign.status === 'draft' && (
                        <button
                          onClick={(e) => delete_draft(campaign.id, e)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete draft"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {}
                  {campaign.category && (
                    <div className="mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                        {cat_id2lbl(campaign.category)}
                      </span>
                    </div>
                  )}

                  {}
                  {campaign.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {campaign.description}
                    </p>
                  )}

                  {}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                        </svg>
                        <span className="text-xs text-gray-600">CPS</span>
                      </div>
                      <div className="text-lg font-bold text-blue-600">
                        {platform.cps_lbl(campaign.budget_info?.creator_tiers)}
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                        </svg>
                        <span className="text-xs text-gray-600">Creators</span>
                      </div>
                      <div className="text-lg font-bold text-purple-600">
                        {campaign.active_creators_count || 0}
                      </div>
                    </div>
                  </div>

                  {}
                  {totalBudget > 0 && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Budget</span>
                        <span className="font-bold text-gray-900">
                          ${totalBudget.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all"
                          style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">
                          Spent: ${spent.toLocaleString()}
                        </span>
                        <span className="text-green-600 font-semibold">
                          Left: ${remaining.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  {}
                  <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    Created {campaign.ts?.insert ? xdate.fmt_lbl_short(campaign.ts.insert) : ''}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}